import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Search, MapPin, Briefcase, ChevronRight, Filter } from 'lucide-react'
import { profileAPI } from '../../services/api'
import { Helmet } from 'react-helmet-async'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'

// Fix for default marker icon in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const CATEGORY_MAP = {
  Automotive: [
    "Auto Accessories",
    "Auto Dealers – New",
    "Auto Dealers – Used",
    "Detail & Carwash",
    "Gas Stations",
    "Motorcycle Sales & Repair",
    "Rental & Leasing",
    "Service, Repair & Parts",
    "Towing",
  ],

  "Business Support & Supplies": [
    "Consultants",
    "Employment Agency",
    "Marketing & Communications",
    "Office Supplies",
    "Printing & Publishing",
  ],

  "Computers & Electronics": [
    "Computer Programming & Support",
    "Consumer Electronics & Accessories",
  ],

  "Construction & Contractors": [
    "Architects, Landscape Architects, Engineers & Surveyors",
    "Blasting & Demolition",
    "Building Materials & Supplies",
    "Construction Companies",
    "Electricians",
    "Engineer, Survey",
    "Environmental Assessments",
    "Inspectors",
    "Plaster & Concrete",
    "Plumbers",
    "Roofers",
  ],

  Education: [
    "Adult & Continuing Education",
    "Early Childhood Education",
    "Educational Resources",
    "Other Educational",
  ],

  Entertainment: [
    "Artists, Writers",
    "Event Planners & Supplies",
    "Golf Courses",
    "Movies",
    "Productions",
  ],

  "Food & Dining": [
    "Desserts, Catering & Supplies",
    "Fast Food & Carry Out",
    "Grocery, Beverage & Tobacco",
    "Restaurants",
  ],

  "Health & Medicine": [
    "Acupuncture",
    "Assisted Living & Home Health Care",
    "Audiologist",
    "Chiropractic",
    "Clinics & Medical Centers",
    "Dental",
    "Diet & Nutrition",
    "Laboratory, Imaging & Diagnostic",
    "Massage Therapy",
    "Mental Health",
    "Nurse",
    "Optical",
    "Pharmacy, Drug & Vitamin Stores",
    "Physical Therapy",
    "Physicians & Assistants",
    "Podiatry",
    "Social Worker",
    "Animal Hospital",
    "Veterinary & Animal Surgeons",
  ],

  "Home & Garden": [
    "Antiques & Collectibles",
    "Cleaning",
    "Crafts, Hobbies & Sports",
    "Flower Shops",
    "Home Furnishings",
    "Home Goods",
    "Home Improvements & Repairs",
    "Landscape & Lawn Service",
    "Pest Control",
    "Pool Supplies & Service",
    "Security System & Services",
  ],

  "Legal & Financial": [
    "Accountants",
    "Attorneys",
    "Financial Institutions",
    "Financial Services",
    "Insurance",
    "Other Legal",
  ],

  "Manufacturing, Wholesale, Distribution": [
    "Distribution, Import/Export",
    "Manufacturing",
    "Wholesale",
  ],

  "Merchants (Retail)": [
    "Cards & Gifts",
    "Clothing & Accessories",
    "Department Stores, Sporting Goods",
    "General",
    "Jewelry",
    "Shoes",
  ],

  Miscellaneous: [
    "Civic Groups",
    "Funeral Service Providers & Cemeteries",
    "Miscellaneous",
    "Utility Companies",
  ],

  "Personal Care & Services": [
    "Animal Care & Supplies",
    "Barber & Beauty Salons",
    "Beauty Supplies",
    "Dry Cleaners & Laundromats",
    "Exercise & Fitness",
    "Massage & Body Works",
    "Nail Salons",
    "Shoe Repairs",
    "Tailors",
  ],

  "Real Estate": [
    "Agencies & Brokerage",
    "Agents & Brokers",
    "Apartment & Home Rental",
    "Mortgage Broker & Lender",
    "Property Management",
    "Title Company",
  ],

  "Travel & Transportation": [
    "Hotel, Motel & Extended Stay",
    "Moving & Storage",
    "Packaging & Shipping",
    "Transportation",
    "Travel & Tourism",
  ],
}

const LOCATIONS = [
  'All',
  'Bangalore',
  'Mumbai',
  'Delhi',
  'New York',
  'London',
  'Remote',
]

const VisitSearchPage = () => {
  const navigate = useNavigate()

  const [searchTerm, setSearchTerm] = useState('')
  const [category, setCategory] = useState('All')
  const [subCategory, setSubCategory] = useState('All')
  const [location, setLocation] = useState('')
  const [profiles, setProfiles] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [showMap, setShowMap] = useState(false)

  const [openNow, setOpenNow] = useState(false)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

   useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(location)
    }, 500)
    return () => clearTimeout(timer)
  }, [location])

  useEffect(() => {
    fetchProfiles()
  }, [debouncedSearch, category, subCategory, location])

  const fetchProfiles = async () => {
    try {
      setIsLoading(true)
      const params = {
        search: debouncedSearch,
        category: category !== 'All' ? category : undefined,
        subcategory: subCategory !== 'All' ? subCategory : undefined,
        location: location !== 'All' ? location : undefined
      }
      
      console.log('Fetching profiles with params:', params); 
      const res = await profileAPI.getPublicProfiles(params)
      console.log('Profile API Response:', res.data);
      if (res.data.success) {
        setProfiles(res.data.data)
      }
    } catch (error) {
      console.error('Failed to fetch profiles', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Helper to check if open now
  const isOpenNow = (businessHours) => {
    if (!businessHours) return false;
    
    const now = new Date();
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = days[now.getDay()];
    const todayHours = businessHours[currentDay];

    if (!todayHours || !todayHours.isOpen) return false;

    const currentTime = now.getHours() * 60 + now.getMinutes();
    const [openHour, openMinute] = todayHours.open.split(':').map(Number);
    const [closeHour, closeMinute] = todayHours.close.split(':').map(Number);
    const openTime = openHour * 60 + openMinute;
    const closeTime = closeHour * 60 + closeMinute;

    return currentTime >= openTime && currentTime <= closeTime;
  };

  const filteredProfiles = openNow 
    ? profiles.filter(p => isOpenNow(p.businessHours))
    : profiles;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20 pb-10 px-4">
      <Helmet>
        <title>Search Businesses - FiindIt</title>
      </Helmet>

      {/* Header & Search */}
      <div className="max-w-4xl mx-auto mb-10">
        <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-blue-400 mb-2">
            Find Professionals & Businesses
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
                Connect with the right people, right now.
            </p>
        </div>
        
        {/* Search & Filter Bar */}
  <div className="bg-white dark:bg-gray-800 p-4 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 space-y-4">

  {/* 🔍 ROW 1: SEARCH (ALL SCREENS) */}
  <div className="w-full">
    <div className="relative">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
      <input
        type="text"
        placeholder="Search name, username..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full pl-12 pr-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-700/50
                   border-none focus:ring-2 focus:ring-primary-500 outline-none
                   text-gray-900 dark:text-white"
      />
    </div>
  </div>

  {/* 🎛 ROW 2: FILTERS */}
  <div className="flex flex-col sm:flex-row gap-3">

    {/* Main Category */}
    <div className="relative w-full sm:w-1/3">
      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
      <select
        value={category}
        onChange={(e) => {
          setCategory(e.target.value)
          setSubCategory('')
        }}
        className="w-full pl-10 pr-8 py-3 rounded-2xl bg-gray-50 dark:bg-gray-700/50
                   border-none focus:ring-2 focus:ring-primary-500 outline-none
                   text-gray-900 dark:text-white appearance-none cursor-pointer"
      >
        <option value="">Category</option>
        {Object.keys(CATEGORY_MAP).map((cat) => (
          <option key={cat} value={cat}>
            {cat}
          </option>
        ))}
      </select>
    </div>

    {/* SubCategory */}
    <div className="relative w-full sm:w-1/3">
      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
      <select
        value={subCategory}
        onChange={(e) => setSubCategory(e.target.value)}
        disabled={!category}
        className="w-full pl-10 pr-8 py-3 rounded-2xl bg-gray-50 dark:bg-gray-700/50
                   border-none focus:ring-2 focus:ring-primary-500 outline-none
                   text-gray-900 dark:text-white appearance-none cursor-pointer
                   disabled:opacity-50"
      >
        <option value="">
          {category ? 'Sub Category' : 'Select Category first'}
        </option>
        {CATEGORY_MAP[category]?.map((sub) => (
          <option key={sub} value={sub}>
            {sub}
          </option>
        ))}
      </select>
    </div>

    {/* Location */}
    <div className="relative w-full sm:w-1/3">
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
      <input
        type="text"
        placeholder="Search location..."
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        className="w-full pl-10 pr-8 py-3 rounded-2xl bg-gray-50 dark:bg-gray-700/50
                   border-none focus:ring-2 focus:ring-primary-500 outline-none
                   text-gray-900 dark:text-white appearance-none cursor-pointer"
      />
    </div>

  </div>

    {/* Open Now Filter */}
    <div className="flex items-center space-x-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <label className="flex items-center space-x-2 cursor-pointer select-none">
            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${openNow ? 'bg-green-500 border-green-500' : 'border-gray-300 dark:border-gray-600'}`}>
                {openNow && <span className="text-white text-xs">✓</span>}
            </div>
            <input 
                type="checkbox" 
                className="hidden" 
                checked={openNow}
                onChange={(e) => setOpenNow(e.target.checked)}
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Open Now</span>
        </label>
    </div>
</div>


    {/* 🎛 Filters */}
    
              </div>

      {/* Toggle View (Hidden for now) */}
      {/* <div className="flex justify-end max-w-7xl mx-auto mb-6 px-2">
            <div className="bg-white dark:bg-gray-800 p-1 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm inline-flex">
                <button 
                    onClick={() => setShowMap(false)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${!showMap ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}
                >
                    List View
                </button>
                <button 
                    onClick={() => setShowMap(true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${showMap ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}
                >
                    Map View
                </button>
            </div>
      </div> */}

      {/* Results */}
      {showMap ? (
           <div className="max-w-7xl mx-auto h-[600px] rounded-3xl overflow-hidden shadow-xl border border-gray-200 dark:border-gray-700 z-0 relative">
               <MapContainer center={[20.5937, 78.9629]} zoom={5} style={{ height: '100%', width: '100%', zIndex: 0 }}>
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {filteredProfiles.map(profile => (
                        profile.coordinates && profile.coordinates.lat && profile.coordinates.lng && (
                            <Marker key={profile._id} position={[profile.coordinates.lat, profile.coordinates.lng]}>
                                <Popup>
                                    <div className="text-center min-w-[150px]">
                                        <div className="w-10 h-10 rounded-full bg-gray-200 mx-auto mb-2 overflow-hidden">
                                            {profile.avatar ? <img src={profile.avatar} className="w-full h-full object-cover"/> : <span className="flex items-center justify-center h-full font-bold text-gray-400">{profile.displayName?.charAt(0)}</span>}
                                        </div>
                                        <h3 className="font-bold text-gray-900">{profile.displayName}</h3>
                                        <p className="text-xs text-gray-500 mb-2">{profile.jobTitle}</p>
                                        <button 
                                            onClick={() => navigate(`/visit/p/${profile.username || profile._id}`)}
                                            className="px-3 py-1.5 bg-blue-500 text-white text-xs font-bold rounded-lg w-full"
                                        >
                                            View Profile
                                        </button>
                                    </div>
                                </Popup>
                            </Marker>
                        )
                    ))}
               </MapContainer>
           </div>
      ) : (
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
           [...Array(6)].map((_, i) => (
             <div key={i} className="bg-white dark:bg-gray-800 h-64 rounded-2xl animate-pulse" />
           ))
        ) : filteredProfiles.length === 0 ? (
            <div className="col-span-full text-center py-20">
                <Filter className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">No profiles found</h3>
                <p className="text-gray-500">Try adjusting your filters or search terms.</p>
            </div>
        ) : (
            filteredProfiles.map((profile) => (
                <motion.div 
                    key={profile._id}
                    whileHover={{ y: -5 }}
                    className={`bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all border cursor-pointer flex flex-col items-center text-center relative overflow-hidden group
                        ${profile.user?.isPro 
                            ? 'border-yellow-400 dark:border-yellow-500 ring-1 ring-yellow-400/30' 
                            : 'border-gray-100 dark:border-gray-700'
                        }
                    `}
                    onClick={() => navigate(`/visit/p/${profile.username || profile._id}`)}
                >
                     {/* Bkg Gradient Accent */}
                     <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-500 to-accent-500 opacity-0 group-hover:opacity-100 transition-opacity" />

                    {/* Pro Badge (Top Right) */}
                    {profile.user?.isPro && (
                        <div className="absolute top-3 right-3 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-yellow-200 dark:border-yellow-700 uppercase tracking-wide flex items-center space-x-1">
                            <span>PRO</span>
                            
                        </div>
                    )}

                    {/* Teams-like Status Indicator (Top Left) */}
                    <div className="absolute top-3 left-3 flex items-center space-x-1.5 bg-white/90 dark:bg-gray-800/90 px-2 py-1 rounded-full shadow-sm border border-gray-100 dark:border-gray-700 backdrop-blur-sm">
                        {(() => {
                            const open = isOpenNow(profile.businessHours);
                            return (
                                <>
                                    <div className={`w-2.5 h-2.5 rounded-full ${open ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                                    <span className={`text-[10px] font-bold uppercase tracking-wider ${open ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                        {open ? 'Available' : 'Offline'}
                                    </span>
                                </>
                            )
                        })()}
                    </div>

                    {/* Avatar */}
                    <div className="relative mt-2">
                        <div className={`w-24 h-24 rounded-full bg-gray-100 dark:bg-gray-700 mb-4 overflow-hidden border-4 shadow-md group-hover:scale-105 transition-transform
                            ${profile.user?.isPro ? 'border-yellow-400 dark:border-yellow-500' : 'border-white dark:border-gray-600'}
                        `}>
                            {profile.avatar ? (
                                <img src={profile.avatar} alt={profile.displayName} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-gray-400 uppercase">
                                    {profile.displayName?.charAt(0)}
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="flex items-center justify-center space-x-1 mb-1">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-primary-500 transition-colors flex items-center gap-1.5">
                            {profile.displayName}
                            {profile.user?.isPro && (
                                <div title="Verified Professional">
                                    <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            )}
                        </h3>
                    </div>

                    <p className="text-primary-600 dark:text-primary-400 text-sm font-medium mb-3">
                        {profile.jobTitle || 'Business Profile'}
                    </p>
                    
                    {/* Status Text (Optional: keep concise card) */}
                    <div className="mt-2" />

                    <div className="space-y-2 text-sm text-gray-500 dark:text-gray-400 w-full mb-6">
                        {profile.company && (
                            <div className="flex items-center justify-center gap-2">
                                <Briefcase className="w-4 h-4" />
                                <span>{profile.company}</span>
                            </div>
                        )}
                        {profile.location && (
                            <div className="flex items-center justify-center gap-2">
                                <MapPin className="w-4 h-4" />
                                <span>{profile.location}</span>
                            </div>
                        )}
                    </div>

                    <button className="mt-auto w-full py-2.5 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-primary-50 dark:hover:bg-primary-900/30 hover:text-primary-600 dark:hover:text-primary-400 transition-all flex items-center justify-center gap-2 font-medium">
                        View Profile <ChevronRight className="w-4 h-4" />
                    </button>
                </motion.div>
            ))
        )}
      </div>
      )}
    </div>
  )
}

export default VisitSearchPage
